import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

import { TextField } from '@components/FormFields';
import CustomSpinner from '@components/Spinner/CustomSpinner';
import { useSubscriptionUpdateForCbpt } from '@hooks';
import { RECAPTCHA_SITE_KEY } from '@src/env';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { buildQueryString, extractQueryParams } from '@utils/queryStringHelper';

import { SubscriptionFormProps } from '.';
import StripeDisclosure from './StripeDisclosure';
import styles from './styles.module.scss';

interface PaymentMethodProps
  extends Omit<SubscriptionFormProps, 'userEmail' | 'userFullName' | 'dob' | 'formLocation'> {
  isReferral?: boolean;
  subscriptionId?: string;
}

interface FormValues {
  username: string;
}

const PaymentMethod = ({
  buttonText,
  isReferral,
  isUserCBPT,
  presentedOfferCodes,
  referringSubscriberName,
  referringSubscriberGuid,
  selectedOffer: { currentPrice, billingFrequency, originalPrice, code },
  subscriptionId,
  userNextBillingDate,
}: PaymentMethodProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: updateSubscriptionForCbpt } = useSubscriptionUpdateForCbpt();
  // const { mutateAsync: verifyReCaptcha } = useVerifyReCaptcha();
  const protocol = window.location.protocol;
  const host = window.location.host;
  const { article_id, modal } = extractQueryParams(['article_id', 'modal']);
  const hasDiscount = currentPrice !== originalPrice;

  const handlePayment = async (): Promise<never | { error: StripeError }> => {
    const queryString = buildQueryString({
      article_id,
      modal,
      code,
      presented_offer_codes: presentedOfferCodes,
      guid: referringSubscriberGuid,
      subscription_id: subscriptionId,
    });

    const returnUrl = `${protocol}//${host}/payment-status?${queryString}`;
    const paymentResponse = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Can't use READER_URL for testing, so this is used to build URLs dynamically
        return_url: returnUrl,
      },
      redirect: 'always',
    });

    return { error: paymentResponse.error };
  };

  const handleSubmit = async ({ username }: FormValues) => {
    // Username is a random hidden field to prevent bots from submitting the form, human users will not see this but bots will
    // so if the field is filled out, we know it's a bot and we don't submit the form.
    if (username) {
      return;
    }
    // Send an empty string for reCaptcha token in test environment so it doesn't fail
    // This Prevent Apple Pay working, we might need to comeback and depending if we are getting bots
    // const reCaptchaToken = await reCaptchaRef.current.executeAsync();
    // reCaptchaRef.current.reset();

    // const { success } = await verifyReCaptcha({ recaptcha_token: reCaptchaToken });
    // if (!success) {
    //   return;
    // }

    if (isUserCBPT) {
      setIsLoading(true);
      updateSubscriptionForCbpt({
        page: window.location.pathname,
        presented_offer_codes: presentedOfferCodes,
        code,
        source: 'web',
      });
      setIsLoading(true);
      return;
    }

    if (!stripe || !elements) {
      setErrorMessage('Still loading Stripe. Please wait a few seconds and then try again.');
      return;
    }

    setIsLoading(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setIsLoading(false);
      return;
    }

    const { error } = await handlePayment();

    setIsLoading(false);
    if (error) {
      setErrorMessage(error.message);
      return;
    }
  };

  return (
    <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
      <Formik initialValues={{ username: '' }} onSubmit={handleSubmit}>
        {() => (
          <Form className={styles.paymentForm}>
            <div className="mb-20 mt-24 flex items-center">
              <div className="font-sans text-body-sm font-bold uppercase text-primary-red">
                Payment Information
              </div>
              <div className="ml-12 h-2 flex-1 bg-primary-red"></div>
            </div>
            <TextField className="hidden" name="username" />
            {!isUserCBPT && (
              <div id="payment-form">
                <PaymentElement
                  onChange={() => setErrorMessage('')}
                  options={{
                    layout: {
                      type: 'tabs',
                      defaultCollapsed: false,
                    },
                  }}
                />
                {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
              </div>
            )}
            <div className={styles.totalContainer}>
              <div className="flex items-start justify-between">
                <div className={styles.todaysTotal}>
                  {isUserCBPT && userNextBillingDate
                    ? `You'll be charged on ${userNextBillingDate}`
                    : "Today's Total"}
                </div>
                <div className="flex flex-col text-right">
                  <span className={styles.amount}>${currentPrice.toFixed(2)}</span>
                </div>
              </div>
              {originalPrice && hasDiscount && billingFrequency && (
                <div className="flex text-gray-500">
                  {billingFrequency} after the offer concludes{' '}
                  <span className="ml-auto">${originalPrice.toFixed(2)}</span>
                </div>
              )}
            </div>
            {isReferral && (
              <div className={styles.chargeNote}>
                By beginning your subscription, we will be sharing your name with{' '}
                {referringSubscriberName}, who has referred you, and letting them know that
                you&apos;ve started enjoying The Information.
              </div>
            )}
            <StripeDisclosure />
            <Button
              disabled={!stripe || isLoading}
              type="submit"
              className={clsx(styles.submitBtn, 'mt-16')}
            >
              {isLoading ? (
                <CustomSpinner
                  color="#fff"
                  size={10}
                  style={{ display: 'flex', justifyContent: 'center' }}
                />
              ) : (
                <>{buttonText}</>
              )}
            </Button>
          </Form>
        )}
      </Formik>
    </ReCAPTCHA>
  );
};

export default PaymentMethod;
